import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, Zap } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";

export const TensionCurveModal = ({
  open,
  onOpenChange,
  tensionCurve,
  setTensionCurve,
  tensionLevelColors,
  tensionLevelNames,
  tensionTemplates,
  loadingTension,
  project,
  projectId,
  API,
  loadTensionCurve,
  selectedTemplate,
  setSelectedTemplate,
  createTensionCurve,
}) => {
  return (
            <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-literary flex items-center gap-2">
              <Zap className="w-5 h-5 text-orange-600" />
              Narrative Tension Curve
            </DialogTitle>
            <DialogDescription>
              Set the emotional pacing for your story. This guides Shweebum to write chapters with the appropriate tension level.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Template Selection */}
            <div>
              <label className="text-sm font-medium text-foreground mb-2 block">Choose a Template</label>
              <div className="grid grid-cols-1 gap-2">
                {Object.entries(tensionTemplates).map(([key, template]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedTemplate(key)}
                    className={`text-left p-3 rounded-lg border transition-all ${
                      selectedTemplate === key 
                        ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-500' 
                        : 'border-border hover:border-orange-300'
                    }`}
                  >
                    <div className="font-medium text-foreground">{template.name}</div>
                    <div className="text-xs text-muted-foreground">{template.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Current Curve Visualization */}
            {tensionCurve && tensionCurve.length > 0 && (
              <div className="bg-background rounded-lg p-4">
                <h4 className="text-sm font-medium text-foreground mb-2">Current Tension Curve</h4>
                {/* Story Beat Labels */}
                <div className="flex justify-between text-xs text-muted-foreground mb-1 px-1">
                  <span>Setup</span>
                  <span>Rising</span>
                  <span>Midpoint</span>
                  <span>Collapse</span>
                  <span>Climax</span>
                </div>
                <div className="flex items-end gap-1 h-24">
                  {tensionCurve.map((t, i) => (
                    <div 
                      key={i} 
                      className="flex-1 rounded-t transition-all hover:opacity-80 cursor-pointer group relative"
                      style={{ 
                        height: `${(t.tension_level / 10) * 100}%`,
                        backgroundColor: tensionLevelColors[t.tension_level] || '#f97316',
                        minHeight: '4%'
                      }}
                      title={`Ch ${t.chapter}: ${tensionLevelNames[t.tension_level] || t.tension_name} (${t.tension_level}/10)`}
                    >
                      <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block bg-black text-white text-xs px-2 py-1 rounded whitespace-nowrap z-20">
                        Ch {t.chapter}: {tensionLevelNames[t.tension_level] || t.tension_name} ({t.tension_level}/10)
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-muted-foreground mt-1">
                  <span>Ch 1 (Start)</span>
                  <span>Ch {tensionCurve.length} (End)</span>
                </div>
              </div>
            )}

            {/* Tension Level Legend */}
            <div className="bg-orange-50 rounded-lg p-3">
              <h4 className="text-xs font-medium text-orange-700 mb-2">Tension Levels</h4>
              <div className="grid grid-cols-4 gap-1 text-xs">
                {Object.entries(tensionLevelColors).map(([level, color]) => (
                  <div key={level} className="flex items-center gap-1">
                    <div className="w-3 h-3 rounded" style={{ backgroundColor: color }}></div>
                    <span className="text-muted-foreground">{level}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                createTensionCurve(selectedTemplate);
                onOpenChange(false);
              }}
              disabled={loadingTension}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {loadingTension ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4 mr-2" />
                  Apply Template
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


  );
};
